import { CSSProperties, FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { mainButton } from '@telegram-apps/sdk-react';

import { useBackButtonToCloseModal } from '@/hooks/useBackButtonToCloseModal.tsx';
import { SpokenLanguages } from '@/enums/apiEnums.ts';
import { useProfileStore } from '@/store/useProfileStore.ts';
import { InputContent } from '@/components/common/InputContent/InputContent.tsx';
import { helpers } from '@/helpers.ts';
import { Toast } from '@/components/Toast/Toast.tsx';
import { MessageTypesUI } from '@/enums/viewEnums.ts';
import {
  iconCircleStyle,
  innerCircleStyle,
  svgContainerStyle,
  titleStyle
} from '@/pages/Profile/Edit/styles.ts';
import {
  containerStyle,
  inputContainerStyle,
  listItemStyle,
  scrollableListStyle,
  selectedItemMarkStyle,
  selectedItemStyle,
  unselectedItemStyle
} from '@/pages/Profile/Edit/EditEmployment/styles.ts';
import SpokenLanguagesShape from '@/components/common/svg/shapes/SpokenLanguagesShape.tsx';

const MAX_LANGUAGES = 5;

export const getSpokenLanguages = (
  t: TFunction
): Record<SpokenLanguages, string> => ({
  [SpokenLanguages.LANGUAGE_UKRAINIAN]: t('language.ukrainian'),
  [SpokenLanguages.LANGUAGE_ENGLISH]: t('language.english'),
  [SpokenLanguages.LANGUAGE_POLISH]: t('language.polish'),
  [SpokenLanguages.LANGUAGE_SPANISH]: t('language.spanish'),
  [SpokenLanguages.LANGUAGE_ITALIAN]: t('language.italian'),
  [SpokenLanguages.LANGUAGE_GERMAN]: t('language.german'),
  [SpokenLanguages.LANGUAGE_FRENCH]: t('language.french'),
  [SpokenLanguages.LANGUAGE_AZERBAIJANI]: t('language.azerbaijani'),
  [SpokenLanguages.LANGUAGE_ARMENIAN]: t('language.armenian'),
  [SpokenLanguages.LANGUAGE_BELORUSSIAN]: t('language.belorussian'),
  [SpokenLanguages.LANGUAGE_GEORGIAN]: t('language.georgian'),
  [SpokenLanguages.LANGUAGE_RUSSIAN]: t('language.russian'),
  [SpokenLanguages.LANGUAGE_HEBREW]: t('language.hebrew'),
  [SpokenLanguages.LANGUAGE_KAZAKH]: t('language.kazakh'),
  [SpokenLanguages.LANGUAGE_KYRGYZ]: t('language.kyrgyz'),
  [SpokenLanguages.LANGUAGE_LATVIAN]: t('language.latvian'),
  [SpokenLanguages.LANGUAGE_LITHUANIAN]: t('language.lithuanian'),
  [SpokenLanguages.LANGUAGE_MOLDAVIAN]: t('language.moldavian'),
  [SpokenLanguages.LANGUAGE_TAJIK]: t('language.tajik'),
  [SpokenLanguages.LANGUAGE_TURKMEN]: t('language.turkmen'),
  [SpokenLanguages.LANGUAGE_UZBEK]: t('language.uzbek'),
  [SpokenLanguages.LANGUAGE_ESTONIAN]: t('language.estonian'),
  [SpokenLanguages.LANGUAGE_GREEK]: t('language.greek'),
  [SpokenLanguages.LANGUAGE_PORTUGUESE]: t('language.portuguese')
});

interface EditSpokenLanguagesPopupProps {
  handleClose: () => void;
}

const EditSpokenLanguagesPopup: FC<EditSpokenLanguagesPopupProps> = ({ handleClose }) => {
  useBackButtonToCloseModal(handleClose, false);
  
  const { t } = useTranslation();
  const { aboutMe, updateProfile } = useProfileStore();
  const spokenLanguages = getSpokenLanguages(t);

  const [isEdited, setIsEdited] = useState<boolean>(false);
  const [selectedSpokenLanguages, setSelectedSpokenLanguages] = useState<
    SpokenLanguages[]
  >(aboutMe?.profile.spoken_languages ?? []);

  const toggleLanguage = (language: SpokenLanguages) => {
    setIsEdited(true);
    setSelectedSpokenLanguages((prev) => {
      if (prev.includes(language)) {
        return prev.filter((lang) => lang !== language);
      } else if (prev.length >= MAX_LANGUAGES) {
        return prev;
      }
      return [...prev, language];
    });
  };

  const submitSpokenLanguages = async () => {
    mainButton.setParams({ isLoaderVisible: true, isEnabled: false });

    try {
      await updateProfile({ spoken_languages: selectedSpokenLanguages });
      helpers.toast({
        id: MessageTypesUI.TYPE_SYSTEM_SUCCESS,
        content: <Toast type={MessageTypesUI.TYPE_SYSTEM_SUCCESS} />
      });
      handleClose();
    } catch {
      helpers.toast({
        id: MessageTypesUI.TYPE_SYSTEM_ERROR,
        content: <Toast type={MessageTypesUI.TYPE_SYSTEM_ERROR} />
      });
    } finally {
      mainButton.setParams({
        isLoaderVisible: false,
        isVisible: false,
        isEnabled: true
      });
    }
  };

  useEffect(() => {
    mainButton.setParams({
      isVisible: isEdited
    });
  }, [isEdited]);

  useEffect(() => {
    mainButton.setParams({
      isEnabled: true,
      backgroundColor: '#000000',
      text: t('button.save')
    });

    mainButton.onClick(submitSpokenLanguages);
    return () => mainButton.offClick(submitSpokenLanguages);
  }, [selectedSpokenLanguages]);

  return (
    <div style={popupContainerStyle}>
      <div style={svgContainerStyle}>
        <div style={iconCircleStyle}>
          <div style={innerCircleStyle}>
            <SpokenLanguagesShape width={25} height={25} fillColor="#fff" />
          </div>
        </div>
        <div style={titleStyle}>
          {t('indicate_spoken_languages')}
          <div style={{ fontSize: '14px', color: '#000' }}>
            ({selectedSpokenLanguages.length}/{MAX_LANGUAGES})
          </div>
        </div>
      </div>
      <div style={inputContainerStyle}>
        <InputContent>
          <div style={containerStyle}>
            <div style={scrollableListStyle}>
              {Object.entries(spokenLanguages).map(([value, label]) => {
                const isSelected = selectedSpokenLanguages.includes(
                  value as SpokenLanguages
                );
                return (
                  <div
                    key={value}
                    onClick={() => toggleLanguage(value as SpokenLanguages)}
                    style={{
                      ...listItemStyle,
                      ...(isSelected ? selectedItemStyle : unselectedItemStyle),
                      position: 'relative',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    {label}
                    {isSelected && <span style={selectedItemMarkStyle}>✓</span>}
                  </div>
                );
              })}
            </div>
          </div>
        </InputContent>
      </div>
    </div>
  );
};

const popupContainerStyle: CSSProperties = {
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  overflow: 'auto'
};

export default EditSpokenLanguagesPopup;
